import { Component, HostListener, Inject, OnInit, PLATFORM_ID, afterNextRender, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsComponent } from './shared/notification/notification.component';
import { HeaderComponent } from './shared/header/header.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MobileFooterMenuComponent } from './shared/mobile-footer-menu/mobile-footer-menu.component';
import { SignalsStoreService } from './shared/signals-store.service';
import { AppService } from './app.service';
import { ResolutionService } from './shared/resolution.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { LocalStorageService } from './shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from './shared/constants/databases';
import { OrderService } from './shared/order.service';
import { KlaviyoService } from './shared/klaviyo/klaviyo.service';
import { AttributionService } from './shared/attribution/attribution.service';
import { ScrollToTopDirective } from './shared/directives/scroll-to-top.directive';
import { ShopSliderBannerComponent } from './shared/shop-slider-banner/shop-slider-banner.component';
import { ShopHeaderComponent } from './shared/shop-header/shop-header.component';
import { ShopBundleListComponent } from './shared/shop-bundle-list/shop-bundle-list.component';
import { GlobalMessageComponent } from './shared/global-message/global-message.component';
import { LoadingComponent } from './shared/loading/loading.component';
import { LoadingService } from './shared/loading.service';
import { PromotionsService } from './shared/promotions.service';
import { MarketingReferral } from './shared/types/common.types';
import { SideCheckoutComponent } from "./side-checkout/side-checkout.component";
import { IntercomCustomLauncherComponent } from './shared/intercom-custom-launcher/intercom-custom-launcher.component';
import { checkSignupCouponCodes } from './shared/common/singup.utils';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatProgressBarModule,
    NgbModule,
    NotificationsComponent,
    HeaderComponent,
    CommonModule,
    MobileFooterMenuComponent,
    ScrollToTopDirective,
    ShopSliderBannerComponent,
    ShopHeaderComponent,
    ShopBundleListComponent,
    GlobalMessageComponent,
    LoadingComponent,
    SideCheckoutComponent,
    IntercomCustomLauncherComponent
  ],
  providers: [AppService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ecommerce';

  private signalStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private appService = inject(AppService);
  private resolutionService = inject(ResolutionService);
  private localStorageService = inject(LocalStorageService);
  private orderService = inject(OrderService);
  private klaviyoService = inject(KlaviyoService);
  #attributionService = inject(AttributionService);
  loadingService = inject(LoadingService);

  #promotionsService = inject(PromotionsService);

  private containerClassesToApply = {
    layoutSignup: 'layout-signup',
    layoutStatic: 'layout-static',
    layoutShop: 'layout-shop',
    layoutSettings: 'layout-settings',
    layoutLogin: 'layout-login',
  };

  private containerClassesXRout: any = {
    '/': this.containerClassesToApply.layoutStatic,
    '/pages': this.containerClassesToApply.layoutStatic,
    '/login': this.containerClassesToApply.layoutLogin,
    '/account-recovery': this.containerClassesToApply.layoutLogin,
    '/terms-and-conditions': this.containerClassesToApply.layoutLogin,
    '/settings': this.containerClassesToApply.layoutSettings,
    '/signup': this.containerClassesToApply.layoutSignup,
    '/shop': this.containerClassesToApply.layoutShop,
    '/favorites': this.containerClassesToApply.layoutShop,
    '/buy-again': this.containerClassesToApply.layoutShop,
    '/order': this.containerClassesToApply.layoutShop,
    '/cost-calculator': this.containerClassesToApply.layoutStatic,
  }

  showAppHeader = signal(false);
  containerClassToAplly = signal('');
  isInBlankComponent = this.signalStoreService.getIsInBlankComponent;
  hasSession = this.signalStoreService.hasSession;
  #hasSession$ = toObservable(this.hasSession);
  isSidebarOpen = this.signalStoreService.isSidebarOpen;
  hasGlobalMessages = computed(() => !!this.signalStoreService.globalMessages().length);
  showCartPreview = this.signalStoreService.showCartPreview;

  showAppCartPreview = computed(() => this.setUpCartPreviewDisplay());
  showJustAddedProduct = computed(() => this.setUpJustAddedProductDisplay());

  shouldShowCategoriesBar = computed(() => this.signalStoreService.shouldShowCategoriesBar());

  isMobile = computed(() => this.resolutionService.isMobile());
  routeSignal = signal(this.router.url);
  permissionsSignal = computed(() => this.signalStoreService.permissions());

  showMobileFooter = this.signalStoreService.isShowingMobileFooter;

  private isMobiles$ = toObservable(this.isMobile);
  private routeSignal$ = toObservable(this.routeSignal);
  private permissionsSignal$ = toObservable(this.permissionsSignal);

  isShopComponent = computed(() => this.#isInShopComponent());

  isShopBannerHidden = computed(() => this.signalStoreService.isShopBannerHidden());
  isShopBannerHidden$ = toObservable(this.isShopBannerHidden);
  shouldHideShopBanner = signal(false);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    // Earliest browser-side hook we have: the first navigation has not resolved
    // yet, so window.location still carries the campaign params of the landing
    // URL. The service no-ops on the server and after the first capture.
    this.#attributionService.capture();

    this.#identifyKlaviyoUser();
    afterNextRender(() => {
      this.appService.getMaintenanceState();
    })
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: any) {
    if (this.showJustAddedProduct()) {
      if (event.target.className.indexOf('just-added-product-notification') > -1) {
        this.signalStoreService.justAddedProduct.set(null)
      }
    }
  }

  ngOnInit(): void {
    // this.appService.getMaintenanceState();
    this.setUpRouteSubscription();

    if (!isPlatformBrowser(this.platformId)) return;
    this.activatedRoute.queryParams.subscribe(params => this.checkMarketingReferral(params));
    this.appService.addClassToBody(environment.config.bodyClass);

    combineLatest([this.isMobiles$, this.routeSignal$, this.permissionsSignal$]).pipe(
      tap(() => this.validateCategoriesBarDisplay())
    ).subscribe()

    this.isShopBannerHidden$.pipe(
      tap((value: boolean) => {
        setTimeout(() => {
          this.shouldHideShopBanner.set(value === true);
        }, 500);
      })
    ).subscribe()
  }

  #isInShopComponent() {
    const route = this.routeSignal();
    const splittedRoute = route.split('/');
    return splittedRoute.length === 2 && (splittedRoute[1] === 'shop' || splittedRoute[1].includes('shop?'));
  }

  private setUpRouteSubscription() {
    this.router.events.subscribe(() => {
      this.showAppHeader.set(this.validateRoutesForHeader());
      // this.showCartPreview.set(this.valiteRoutesForCartPreview());
      this.containerClassToAplly.set(this.setUpContainerClass());
      this.signalStoreService.isInOrderComponent.set(this.validateIsInRouteComponent('/order'));
      this.routeSignal.set(this.router.url);
    });
  }

  private setUpContainerClass(): string {
    const currentUrl = this.router.url.split('?')[0];
    for (let key in this.containerClassesXRout) {
      if (key === '/' ? currentUrl === key : currentUrl.includes(key)) {
        return this.containerClassesXRout[key];
      }
    }

    return this.containerClassesToApply.layoutShop; // This class by default;
  }

  validateRoutesForHeader(): boolean {
    const allowedRoutes = [
      '/',
      '/order',
      '/pages',
      '/product',
      '/settings',
      '/shop',
      '/favorites',
      '/buy-again',
      '/cart',
      '/welcome',
      '/cost-calculator',
    ];
    return this.validateRoutesToShowComponent(allowedRoutes);
  }

  // valiteRoutesForCartPreview(): boolean {
  //   const allowedRoutes = [
  //     '/product',
  //     '/shop',
  //     '/favorites',
  //     '/buy-again'
  //   ];
  //   return this.validateRoutesToShowComponent(allowedRoutes, 'custom-box');
  // }

  validateRoutesToShowComponent(allowedRoutes: string[], notAllowedRoute: string = 'null'): boolean {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return allowedRoutes.some(route => (route === '/' ? cleanedRoute === route : cleanedRoute.includes(route)) && !cleanedRoute.includes(notAllowedRoute));
  }

  validateIsInRouteComponent(route: string) {
    const currentUrl = this.routeSignal();
    const cleanedRoute = currentUrl.split('?')[0];
    return cleanedRoute === route;
  }

  private setUpCartPreviewDisplay() {
    const hasSession = this.signalStoreService.hasSession();
    // const order = this.signalStoreService.firebaseOrder();
    // const odooOrder = this.orderService.odooOrder();
    // const hasProducts = order?.products?.common?.length || order?.products?.subscription?.length;
    // const hasOdooProducts = odooOrder?.products?.common?.length || odooOrder?.products?.subscription?.length;
    const shouldShowCartPreview = this.showCartPreview();
    return hasSession && shouldShowCartPreview && !this.isMobile();
  }

  private setUpJustAddedProductDisplay() {
    return this.signalStoreService.hasSession() &&
      this.signalStoreService.justAddedProduct() &&
      this.isMobile();
  }

  private validateCategoriesBarDisplay() {
    let signupCustomBox = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_CUSTOM_BOX);
    if (signupCustomBox && this.routeSignal().length > 1 && !(this.routeSignal().includes('/shop/custom-box/subscription'))) {
      this.localStorageService.remove(LOCALSTORAGE_KEYS.SIGNUP_CUSTOM_BOX);
    }

    const isCustomFromWelcome = this.localStorageService.get(LOCALSTORAGE_KEYS.WELCOME_CUSTOM_BOX);

    if (isCustomFromWelcome)
      this.signalStoreService.isCustomBoxSignupFlow.set(false);
    else
      this.signalStoreService.isCustomBoxSignupFlow.set(!!signupCustomBox);

    const isMobile = this.isMobile();

    const shouldHideHeaderCategoriesNav = !!!(this.signalStoreService.permissions()?.shop.allowed ?? true);
    if (shouldHideHeaderCategoriesNav) {
      this.signalStoreService.shouldShowCategoriesBar.set(false);
    } else {
      const shouldShowCategoriesBar = (!isMobile || this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/', '/pages', '/cost-calculator']));
      this.signalStoreService.shouldShowCategoriesBar.set(shouldShowCategoriesBar);
    }

    const shouldShowMobileFooter = isMobile && !shouldHideHeaderCategoriesNav && this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/settings', '/order', '/cart']);
    this.showMobileFooter.set(shouldShowMobileFooter);
  }


  #identifyKlaviyoUser() {
    this.#hasSession$.pipe(
      tap((hasSession: boolean) => {
        if (hasSession)
          this.klaviyoService.identiyUser();
      })
    ).subscribe();
  }

  checkMarketingReferral(params: any) {

    let marketingReferral: MarketingReferral;

    const currentMarketingInfo: MarketingReferral | null = this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);

    if (params['utm_source'] && params['utm_medium'] && params['utm_campaign']) {
      marketingReferral = {
        utmCampaign: params['utm_campaign'],
        utmMedium: params['utm_medium'],
        utmSource: params['utm_source'],
      }
    } else if (currentMarketingInfo?.utmCampaign)
      marketingReferral = { ...currentMarketingInfo };
    else return this.signalStoreService.setIsLoadedMarketingValidation(true);

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: false
    }).subscribe();

  }
}
